import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, CheckCircle2, PhoneCall, XCircle, Loader2 } from 'lucide-react';
import { checkArea } from '@/lib/api';
import { BUSINESS } from '@/data/site';
import { track } from '@/lib/seo';

export default function ZipChecker({ compact = false }) {
  const [zip, setZip] = useState(() => sessionStorage.getItem('mg_area_zip') || '');
  const [result, setResult] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('mg_area_result')); } catch (e) { return null; }
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await checkArea({ zip });
      setResult(res);
      if (res.status !== 'invalid') {
        sessionStorage.setItem('mg_area_zip', zip);
        sessionStorage.setItem('mg_area_result', JSON.stringify(res));
      }
      track('service_area_check', { zip, status: res.status });
    } catch (err) {
      setResult({ status: 'invalid', message: 'Something went wrong. Please try again or call us.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="zip-checker" className={compact ? '' : 'w-full max-w-xl'}>
      <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <MapPin className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ash" />
          <input
            data-testid="zip-checker-input"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
            placeholder="Enter your ZIP code"
            inputMode="numeric"
            aria-label="ZIP code"
            className="w-full border border-ink/20 bg-ink/5 py-4 pl-11 pr-4 text-sm text-ink placeholder:text-ash focus:border-gold focus:outline-none"
          />
        </div>
        <button
          data-testid="zip-checker-submit-button"
          type="submit"
          disabled={loading || zip.length !== 5}
          className="flex items-center justify-center gap-2 bg-gold px-8 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-bronzehover disabled:opacity-40"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Check Availability'}
        </button>
      </form>

      <AnimatePresence mode="wait">
        {result && (
          <motion.div
            key={result.status + result.message}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            data-testid={`zip-result-${result.status}`}
            className={`mt-4 flex items-start gap-3 border p-4 text-sm ${
              result.status === 'served'
                ? 'border-moss/50 bg-forest/30 text-ink'
                : result.status === 'call'
                ? 'border-gold/40 bg-gold/5 text-ink'
                : 'border-oxblood/50 bg-oxblood/10 text-ink'
            }`}
          >
            {result.status === 'served' ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-moss" />
            ) : result.status === 'call' ? (
              <PhoneCall className="mt-0.5 h-5 w-5 shrink-0 text-bronze" />
            ) : (
              <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-oxblood" />
            )}
            <div>
              <p className="font-medium">{result.message}</p>
              {result.status !== 'served' && (
                <a href={BUSINESS.phoneHref} data-testid="zip-result-phone-link" className="mt-1 inline-block font-semibold text-bronze underline underline-offset-4">
                  Call {BUSINESS.phone}
                </a>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
